// src/App.jsx
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import "./App.css";
import Dashboard from "./pages/Dashboard";
import Bookmarks from "./pages/Bookmarks";
import ForgotPassword from "./pages/ForgotPassword";
import ViewBookmarks from "./pages/ViewBookmarks";
import Library from "./pages/Library";
import Settings from "./pages/Settings";
import BookInfo from "./pages/BookInfoPage";
import BookInformationPage from "./pages/BookInformationPage";
import AudioBookPlayer from "./pages/AudioBookPlayer";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import GenreSelection from "./pages/GenreSelect";
import SearchedBooksPage from "./pages/SearchedBooksPage.js";
import ProtectedRoute from "./Routing/PrivateRouting";
import NotFound from "./components/NotFound.js";
import LandingPage from "./pages/LandingPage.js";
import AboutPage from "./pages/About.js";
import SearchedBookInfoPage from "./pages/SearchedBooksInfoPage.js";
import HelpPage from "./pages/HelpPage.js";
import AdminSignIn from "./pages/AdminSignIn.js";
import UserManagement from "./pages/UserManagement.js";
import BookManagement from "./pages/BookManagement.js";
import { getToken } from "./storage/Storage"; // <- import getToken

/* ------------------ global hook: map arrows to tabbing ------------------ */
function useArrowAsTab() {
  useEffect(() => {
    const focusableSelector = [
      'a[href]:not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(",");

    function isTypingElement(el) {
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      const role = el.getAttribute?.("role");

      if (el.dataset && el.dataset.arrowTab === "true") return false;

      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el.isContentEditable) return true;
      if (role === "textbox") return true;
      if (el.closest && el.closest(".no-arrow-tab")) return true;
      return false;
    }

    function getVisibleFocusableElements() {
      const nodes = Array.from(document.querySelectorAll(focusableSelector));
      return nodes.filter((el) => {
        const style = window.getComputedStyle(el);
        if (style.visibility === "hidden" || style.display === "none") return false;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0 && !el.hasAttribute("href")) return false;
        if (el.hasAttribute("disabled")) return false;
        return true;
      });
    }

    function handleKeyDown(e) {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      const active = document.activeElement;
      const focusables = getVisibleFocusableElements();

      if (isTypingElement(active)) return;

      if (focusables.length === 0) return;
      const idx = focusables.indexOf(active);

      let targetIndex;
      if (e.key === "ArrowUp") {
        targetIndex = idx === -1 ? 0 : (idx + 1) % focusables.length;
      } else {
        targetIndex = idx === -1 ? focusables.length - 1 : (idx - 1 + focusables.length) % focusables.length;
      }

      const target = focusables[targetIndex];
      if (target && target !== active) {
        e.preventDefault();
        target.focus({ preventScroll: false });
      }
    }

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, []);
}

/* -------------------------------------------------------------------------- */

function NotFoundWrapper() {
  const location = useLocation();
  return <NotFound location={location} />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/signup" element={<SignUp />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/genre" element={<GenreSelection />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/admin" element={<AdminSignIn />} />

      {/* Protected Routes */}
      <Route path="/User-Management" element={<ProtectedRoute element={UserManagement} />} />
      <Route path="/Book-Management" element={<ProtectedRoute element={BookManagement} />} />
      <Route path="/dashboard" element={<ProtectedRoute element={Dashboard} />} />
      <Route path="/bookmarks" element={<ProtectedRoute element={Bookmarks} />} />
      <Route path="/view-bookmarks" element={<ProtectedRoute element={ViewBookmarks} />} />
      <Route path="/library" element={<ProtectedRoute element={Library} />} />
      <Route path="/settings" element={<ProtectedRoute element={Settings} />} />
      <Route path="/help" element={<ProtectedRoute element={HelpPage} />} />
      <Route path="/book-info/:genre/:bookId" element={<ProtectedRoute element={BookInfo} />} />
      <Route path="/book-info/:bookId" element={<ProtectedRoute element={BookInformationPage} />} />
      <Route path="/searched-books/:bookId" element={<ProtectedRoute element={SearchedBookInfoPage} />} />
      <Route path="/audiobook-player" element={<ProtectedRoute element={AudioBookPlayer} />} />
      <Route path="/searched-results" element={<ProtectedRoute element={SearchedBooksPage} />} />
      <Route path="*" element={<NotFoundWrapper />} />
    </Routes>
  );
}

export default function App() {
  useArrowAsTab();

  // Ensure isAudioModeOn exists and default to false on app mount.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Prefer validated token check (removes expired tokens internally).
        const token = await getToken();

        // If key already present, do nothing
        const existing = localStorage.getItem("isAudioModeOn");
        if (existing === null) {
          // If there's a valid token or even if there's no token, set default false.
          if (!mounted) return;
          localStorage.setItem("isAudioModeOn", JSON.stringify(false));
          // optional: console for dev
          console.log("isAudioModeOn not found — defaulting to false on app mount");
        } else {
          // key exists — nothing to do
          if (!mounted) return;
        }
      } catch (err) {
        // defensive fallback: ensure the key exists
        try {
          if (localStorage.getItem("isAudioModeOn") === null) {
            localStorage.setItem("isAudioModeOn", JSON.stringify(false));
            console.log("Fallback: set isAudioModeOn = false");
          }
        } catch (e) { /* ignore */ }
      }
    })();

    return () => { mounted = false; };
  }, []);

  return (
    <Router basename={process.env.PUBLIC_URL || "/"}>
      <div className="pagesall">
        <AppRoutes />
      </div>
    </Router>
  );
}
